import React, { useEffect, useRef, useState } from 'react';
import { ChevronDown, Building2, Globe } from 'lucide-react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { getOrganisations } from '@/services/organisations';
import { useAuthStore } from '@/hooks/useAuthStore';
import { useAuthPermissions } from '@/hooks/useAuthPermissions';

/**
 * OrgSelector, SUPER_ADMIN only.
 *
 * Renders a compact dropdown listing all child organisations.
 * Selecting one sets scopedOrgId in the auth store, sends X-Organisation-Id
 * on all subsequent API calls (via the Axios interceptor), and invalidates
 * all React Query caches so every page refetches for the new org.
 *
 * The Dashboard page intentionally strips X-Organisation-Id so it always
 * shows platform-wide data regardless of the org selected here.
 */
const OrgSelector: React.FC = () => {
  const { isSuperAdmin } = useAuthPermissions();
  const scopedOrgId = useAuthStore((s) => s.scopedOrgId);
  const setScopedOrg = useAuthStore((s) => s.setScopedOrg);
  const queryClient = useQueryClient();

  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const { data: orgs = [] } = useQuery({
    queryKey: ['organisations'],
    queryFn: getOrganisations,
    staleTime: 10 * 60 * 1000,
    enabled: isSuperAdmin,
  });

  // Allow scopedOrgId to be null to represent 'All organisations'

  // Close dropdown when clicking outside
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  if (!isSuperAdmin) return null;

  const selectedOrg = orgs.find((o) => o.id === scopedOrgId);

  const handleSelect = (orgId: number | null) => {
    if (orgId === scopedOrgId) {
      setIsOpen(false);
      return;
    }
    setScopedOrg(orgId);
    setIsOpen(false);
    // Clear all cached query data so every active query re-fetches from scratch
    // with the new X-Organisation-Id header that the Axios interceptor will now send.
    // Background invalidation is insufficient, it shows stale data while fetching.
    // Dashboard queries will now respect the scoped org context, as globalConfig
    // will be removed from dashboard.ts
    queryClient.clear();
  };

  return (
    <div ref={containerRef} className="relative">
      <button
        onClick={() => setIsOpen((o) => !o)}
        className="flex items-center gap-2 h-9 px-3 rounded-xl border border-gray-200 bg-white hover:bg-gray-50 hover:border-gray-300 transition-all text-sm font-medium text-gray-700 shadow-sm min-w-[160px] max-w-[240px]"
        title="Switch organisation context"
      >
        {selectedOrg ? (
          <Building2 className="w-3.5 h-3.5 text-blue-600 flex-shrink-0" />
        ) : (
          <Globe className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
        )}
        <span className="truncate flex-1 text-left">
          {selectedOrg ? selectedOrg.name : 'All organisations'}
        </span>
        <ChevronDown
          className={`w-3.5 h-3.5 text-gray-400 flex-shrink-0 transition-transform ${isOpen ? 'rotate-180' : ''}`}
        />
      </button>

      {isOpen && (
        <div className="absolute top-full mt-1.5 left-0 z-[200] w-64 bg-white rounded-xl border border-gray-200 shadow-xl py-1.5 overflow-hidden">
          <div className="px-3 py-1.5 text-xs font-semibold text-gray-400 uppercase tracking-wide border-b border-gray-100 mb-1">
            Organisation context
          </div>

          {orgs.length === 0 && <div className="px-3 py-3 text-sm text-gray-500">Loading...</div>}

          <div className="max-h-72 overflow-y-auto">
            <button
              onClick={() => handleSelect(null)}
              className={`w-full flex items-center gap-2.5 px-3 py-2.5 text-left text-sm transition-colors hover:bg-gray-50 ${
                scopedOrgId === null ? 'bg-blue-50 text-blue-700 font-medium' : 'text-gray-700'
              }`}
            >
              <div
                className={`w-6 h-6 rounded-md flex items-center justify-center text-xs font-bold flex-shrink-0 ${
                  scopedOrgId === null ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600'
                }`}
              >
                <Globe className="w-3.5 h-3.5" />
              </div>
              <span className="truncate">All organisations</span>
              {scopedOrgId === null && (
                <span className="ml-auto text-xs text-blue-500">Active</span>
              )}
            </button>

            {orgs.map((org) => (
              <button
                key={org.id}
                onClick={() => handleSelect(org.id)}
                className={`w-full flex items-center gap-2.5 px-3 py-2.5 text-left text-sm transition-colors hover:bg-gray-50 ${
                  scopedOrgId === org.id ? 'bg-blue-50 text-blue-700 font-medium' : 'text-gray-700'
                }`}
              >
                <div
                  className={`w-6 h-6 rounded-md flex items-center justify-center text-xs font-bold flex-shrink-0 ${
                    scopedOrgId === org.id ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600'
                  }`}
                >
                  {org.name.charAt(0).toUpperCase()}
                </div>
                <span className="truncate">{org.name}</span>
                {scopedOrgId === org.id && (
                  <span className="ml-auto text-xs text-blue-500">Active</span>
                )}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default OrgSelector;
